import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import Moment from 'react-moment';

import {
  fetchPlayerOverview,
  fetchPlayerParameters,
  fetchPlayerMatchStats,
  fetchMarkersBulk,
  createVideoList,
} from './api';
import { useMappedPlayerId } from './hooks';
import {
  collectMarkerIds,
  buildMarkerParamIndex,
  buildEventsFromMarkers,
  groupEventsByMatch,
  formatSeconds,
  BODY_PARTS,
  bodyPartLexId,
} from './matchEvents';
import { useLexicon } from '../../hooks/useLexicon';
import {
  buildParamGroupsForPlayer,
} from '../../helpers/parameterGroups';
import { retryAsync } from '../../helpers/retryAsync';
import PlayerLoader, { InlinePlayerLoader } from '../../components/PlayerLoader';
import play from '../../images/ic_play-button.svg';

// Split the matchStats fetch into parallel chunks of this many paramIds each.
// Verified on stage 2026-05-26 with p-trial: single 268-param request ≈ 6.5s,
// three 90-param chunks in parallel ≈ 1.5s. Bigger seasons (Bayern player
// with 49 matches) previously hit the 20s mark — chunked fetch keeps the
// wall time bounded by the slowest individual chunk.
const PARAM_CHUNK_SIZE = 90;

// Cap each /player/markers_search body at this many marker IDs. Pavlović's
// 48-match Bundesliga season produces ~7,700 markers when "All competitions"
// is selected and the stage API silently 502s on a single request that
// large — the symptom the client reported as "events spinner never stops,
// then 'No events'". The matchStats chunked fetch (commit 64a35bc) made
// the per-match data reliably arrive for the full season, which is what
// pushed the union past the server's per-request budget — so this is the
// follow-up that finally unsticks SMRTSTATS-312 #2.
const MARKERS_CHUNK_SIZE = 1500;

const extractMatchStatsRows = (res) => {
  const body = res?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.results)) return body.results;
  if (Array.isArray(body?.data)) return body.data;
  return [];
};

// Chunked matchStats fetch. Splits paramIds into PARAM_CHUNK_SIZE batches and
// fires them in parallel; merges the per-match `parameters` arrays from
// each chunk back into one row per match (joining on match id).
const fetchMatchStatsChunked = async (mappedPlayerId, seasonId, paramIds) => {
  if (!Array.isArray(paramIds) || paramIds.length === 0) return [];
  const chunks = [];
  for (let i = 0; i < paramIds.length; i += PARAM_CHUNK_SIZE) {
    chunks.push(paramIds.slice(i, i + PARAM_CHUNK_SIZE));
  }
  const responses = await Promise.all(
    chunks.map((chunk) => retryAsync(() => fetchPlayerMatchStats(mappedPlayerId, seasonId, chunk))),
  );
  const merged = new Map();
  responses.forEach((res) => {
    extractMatchStatsRows(res).forEach((row) => {
      if (row?.id == null) return;
      const existing = merged.get(row.id);
      if (existing) {
        existing.parameters = (existing.parameters || []).concat(row.parameters || []);
      } else {
        merged.set(row.id, { ...row, parameters: [...(row.parameters || [])] });
      }
    });
  });
  return Array.from(merged.values());
};

// /player/events — cross-match aggregator. The platform's
// /tournaments/player/{id}#events page, scoped to the logged-in player and
// reshaped for mobile per spec §9.
//
// v1 scope: season + match selector, parameter category filter, events list,
// "Watch N events" CTA that creates a share-link token and routes internally
// to /player/events/video. Pitch view, heat zones, body-part filter, and time
// slider are deferred — see specs/.../events-implementation-progress.md §Phase 4.

// SMRTSTATS-297: synthetic "Goals" pseudo-category. Players asked for a
// dedicated chip — the real backend group is "Goals/Chances/Shots" which
// also includes shots/opportunities, so it doesn't surface goals quickly.
const GOALS_CATEGORY = 'Goals';
const GOALS_PARAM_IDS = new Set([2, 3]); // 2 = Goals +, 3 = Goals Penalties +

// sessionStorage keys for filter persistence. Without these, hitting the
// browser-back arrow on the video player wipes all selections on remount.
const SS_KEYS = {
  season: 'player_events_season_id',
  competition: 'player_events_competition_id',
  matches: 'player_events_match_ids',
  category: 'player_events_category',
  params: 'player_events_param_ids',
  mode: 'player_events_mode',
  tone: 'player_events_tone',
  bodyPart: 'player_events_body_part',
  start: 'player_events_start',
  end: 'player_events_end',
  selectedEvents: 'player_events_selected_event_keys',
};

const readJsonArray = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
};

const PlayerEvents = () => {
  const l = useLexicon();
  const mappedPlayerId = useMappedPlayerId();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [overview, setOverview] = useState(null);
  const [parameters, setParameters] = useState(null);
  const [matchStats, setMatchStats] = useState(null);
  const [selectedSeasonId, setSelectedSeasonId] = useState(
    () => sessionStorage.getItem(SS_KEYS.season) || null
  );
  const [selectedCompetitionId, setSelectedCompetitionId] = useState(
    () => sessionStorage.getItem(SS_KEYS.competition) || ''
  );
  const [selectedMatchIds, setSelectedMatchIds] = useState(
    () => new Set(readJsonArray(SS_KEYS.matches).map(String))
  );
  const [startDate, setStartDate] = useState(
    () => sessionStorage.getItem(SS_KEYS.start) || null
  );
  const [endDate, setEndDate] = useState(
    () => sessionStorage.getItem(SS_KEYS.end) || null
  );
  const [selectedCategory, setSelectedCategory] = useState(
    () => sessionStorage.getItem(SS_KEYS.category) || null
  );
  const [selectedParamIds, setSelectedParamIds] = useState(
    () => new Set(readJsonArray(SS_KEYS.params).map(Number))
  );
  // Quick-access filters. 'all' is the default no-op; values are persisted in
  // sessionStorage so the player keeps their preference across navigation.
  const [selectedMode, setSelectedMode] = useState(
    () => sessionStorage.getItem(SS_KEYS.mode) || 'all'
  );
  const [selectedTone, setSelectedTone] = useState(
    () => sessionStorage.getItem(SS_KEYS.tone) || 'all'
  );
  // 1=right foot, 2=left foot, 3=header, 4=body. null = all body parts.
  const [selectedBodyPart, setSelectedBodyPart] = useState(() => {
    const raw = sessionStorage.getItem(SS_KEYS.bodyPart);
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  });
  const [bodyPartOpen, setBodyPartOpen] = useState(false);
  const [selectedEventKeys, setSelectedEventKeys] = useState(
    () => new Set(readJsonArray(SS_KEYS.selectedEvents).map(String))
  );
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creatingShareLink, setCreatingShareLink] = useState(false);

  // Honor ?param=N from the Home KPI tiles on first arrival. Overrides any
  // persisted category/param filter so the deep-link is the authoritative
  // selection; the URL param is then stripped so a back-nav round-trip
  // doesn't re-apply it on every remount.
  const initialParamApplied = useRef(false);
  useEffect(() => {
    if (initialParamApplied.current) return;
    const paramStr = searchParams.get('param');
    if (!paramStr) return;
    const paramId = Number(paramStr);
    if (!Number.isFinite(paramId)) return;
    initialParamApplied.current = true;
    // GOALS is a synthetic category — match it on the goals param ids.
    if (GOALS_PARAM_IDS.has(paramId)) {
      setSelectedCategory(GOALS_CATEGORY);
      setSelectedParamIds(new Set());
    } else {
      setSelectedCategory(null);
      setSelectedParamIds(new Set([paramId]));
    }
    setSelectedEventKeys(new Set());
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  // Bootstrap: fetch overview (matches list + seasons), parameters, then per-season match stats.
  //
  // First-mount race fix (SMRTSTATS-297 client report): when `selectedSeasonId`
  // starts null, the inner `setSelectedSeasonId` used to fire BEFORE we'd
  // awaited matchStats. That setState triggered this effect's cleanup
  // (cancelled=true) and a re-run; the matchStats fetch from the first run
  // then bailed via `if (cancelled) return;` and matchStats stayed null. The
  // second run did eventually populate it — which is why the client report
  // says "reselection fixes it." We now: (a) resolve the seasonId in a local
  // and use it for the matchStats fetch directly, (b) set matchStats even
  // when cancelled so the very first run leaves usable data behind, and
  // (c) set `selectedSeasonId` at the very end so the cleanup-then-rerun
  // happens AFTER matchStats is committed.
  useEffect(() => {
    if (!mappedPlayerId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      retryAsync(() => fetchPlayerOverview(mappedPlayerId, selectedSeasonId || undefined)),
      retryAsync(() => fetchPlayerParameters()),
    ])
      .then(async ([overviewRes, paramsRes]) => {
        if (cancelled) return;
        const overviewData = overviewRes.data;
        const paramsData = paramsRes.data?.results ?? paramsRes.data ?? [];
        setOverview(overviewData);
        setParameters(paramsData);

        const resolvedSeasonId = selectedSeasonId
          || overviewData?.selected_season?.id
          || overviewData?.available_seasons?.[0]?.id;

        const paramIds = (Array.isArray(paramsData) ? paramsData : []).map((p) => p.id);
        let stats = [];
        if (resolvedSeasonId && paramIds.length > 0) {
          // Parallel chunked fetch — total wall time ≈ slowest single chunk,
          // not sum of all params. Cuts the heavy-season Events bootstrap
          // from ~20s to ~5s in the worst case.
          stats = await fetchMatchStatsChunked(mappedPlayerId, resolvedSeasonId, paramIds);
        }
        // Commit matchStats unconditionally — even if this effect was
        // cancelled by the setSelectedSeasonId below, we leave usable data
        // behind for the re-run to find via React's state.
        setMatchStats(stats);

        if (resolvedSeasonId && !selectedSeasonId) {
          setSelectedSeasonId(String(resolvedSeasonId));
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [mappedPlayerId, selectedSeasonId]);

  const seasons = useMemo(
    () => (Array.isArray(overview?.available_seasons) ? overview.available_seasons : []),
    [overview?.available_seasons]
  );

  const matchesAllInSeason = useMemo(() => {
    const list = Array.isArray(overview?.matches_list) ? overview.matches_list : [];
    return [...list].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [overview?.matches_list]);

  // Competitions available in the current season, derived from the match list.
  // Same id-fallback pattern as Matches.jsx (some payloads carry competition_id,
  // others put the string in `competition`).
  const competitions = useMemo(() => {
    const map = new Map();
    matchesAllInSeason.forEach((m) => {
      const id = m.competition_id ?? m.competition;
      const name = typeof m.competition === 'string'
        ? m.competition
        : m.competition?.name;
      if (id != null && name && !map.has(String(id))) {
        map.set(String(id), { id: String(id), name });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [matchesAllInSeason]);

  // Drop the stored competition filter if the current season doesn't contain it
  // (competition IDs do not carry across seasons). Also wipe any persisted
  // date range when this fires — otherwise the previous comp's narrow
  // window survives the auto-clear and silently filters out matches
  // that DO belong to the season but fall outside it.
  useEffect(() => {
    if (!selectedCompetitionId) return;
    if (competitions.length === 0) return;
    const stillThere = competitions.some((c) => c.id === selectedCompetitionId);
    if (!stillThere) {
      setSelectedCompetitionId('');
      setStartDate(null);
      setEndDate(null);
    }
  }, [competitions, selectedCompetitionId]);

  // Belt-and-suspenders: reset the date range on any post-mount change to
  // season OR competition. The user-driven onChange handlers already do
  // this, but tracking it here too covers code-driven changes (the stale-
  // comp drop above, the bootstrap effect setting selectedSeasonId for the
  // first time on first arrival, etc.). The initial-mount guard preserves
  // sessionStorage-restored dates on first paint — the dateBounds clamp
  // effect then takes care of any out-of-range values.
  //
  // Without this, picking a competition with a narrow date window (e.g.
  // a knockout-only cup) and then switching back to "All competitions"
  // could leave the old narrow dates active, hiding matches that DO exist
  // in the broader filter — the user-reported "All competitions doesn't
  // load all matches" bug.
  const skipDateResetOnMount = useRef(true);
  useEffect(() => {
    if (skipDateResetOnMount.current) {
      skipDateResetOnMount.current = false;
      return;
    }
    setStartDate(null);
    setEndDate(null);
  }, [selectedSeasonId, selectedCompetitionId]);

  useEffect(() => {
    if (selectedCompetitionId) {
      sessionStorage.setItem(SS_KEYS.competition, selectedCompetitionId);
    } else {
      sessionStorage.removeItem(SS_KEYS.competition);
    }
  }, [selectedCompetitionId]);

  useEffect(() => {
    sessionStorage.setItem(SS_KEYS.mode, selectedMode);
  }, [selectedMode]);

  useEffect(() => {
    sessionStorage.setItem(SS_KEYS.tone, selectedTone);
  }, [selectedTone]);

  // Persist remaining filter dimensions so a Watch→back navigation lands the
  // player back in the same view they left. Without these, the v1 PWA reset
  // every selection on remount (SMRTSTATS-297 Events §3).
  useEffect(() => {
    if (selectedSeasonId) sessionStorage.setItem(SS_KEYS.season, selectedSeasonId);
    else sessionStorage.removeItem(SS_KEYS.season);
  }, [selectedSeasonId]);

  useEffect(() => {
    sessionStorage.setItem(SS_KEYS.matches, JSON.stringify(Array.from(selectedMatchIds)));
  }, [selectedMatchIds]);

  useEffect(() => {
    if (selectedCategory) sessionStorage.setItem(SS_KEYS.category, selectedCategory);
    else sessionStorage.removeItem(SS_KEYS.category);
  }, [selectedCategory]);

  useEffect(() => {
    sessionStorage.setItem(SS_KEYS.params, JSON.stringify(Array.from(selectedParamIds)));
  }, [selectedParamIds]);

  useEffect(() => {
    if (selectedBodyPart == null) sessionStorage.removeItem(SS_KEYS.bodyPart);
    else sessionStorage.setItem(SS_KEYS.bodyPart, String(selectedBodyPart));
  }, [selectedBodyPart]);

  useEffect(() => {
    if (startDate) sessionStorage.setItem(SS_KEYS.start, startDate);
    else sessionStorage.removeItem(SS_KEYS.start);
  }, [startDate]);

  useEffect(() => {
    if (endDate) sessionStorage.setItem(SS_KEYS.end, endDate);
    else sessionStorage.removeItem(SS_KEYS.end);
  }, [endDate]);

  useEffect(() => {
    sessionStorage.setItem(
      SS_KEYS.selectedEvents,
      JSON.stringify(Array.from(selectedEventKeys))
    );
  }, [selectedEventKeys]);

  // Matches narrowed by competition filter (date-range filtering happens next).
  const matchesInSeasonForCompetition = useMemo(() => {
    if (!selectedCompetitionId) return matchesAllInSeason;
    return matchesAllInSeason.filter((m) => {
      const id = m.competition_id ?? m.competition;
      return String(id) === selectedCompetitionId;
    });
  }, [matchesAllInSeason, selectedCompetitionId]);

  // Date range bounds from the season's match dates (after competition filter).
  // Drives the from/to input min/max and the default values when the season
  // or competition selection changes.
  const dateBounds = useMemo(() => {
    if (matchesInSeasonForCompetition.length === 0) return { min: null, max: null };
    const dates = matchesInSeasonForCompetition.map((m) => String(m.date).slice(0, 10)).sort();
    return { min: dates[0], max: dates[dates.length - 1] };
  }, [matchesInSeasonForCompetition]);

  // Clamp start/end to the season's bounds. Pure reset on every bounds change
  // would clobber sessionStorage-restored values on first load, so only
  // overwrite when the persisted value falls outside the new range or is null.
  useEffect(() => {
    setStartDate((cur) => {
      if (dateBounds.min == null) return null;
      if (!cur || cur < dateBounds.min || cur > dateBounds.max) return dateBounds.min;
      return cur;
    });
    setEndDate((cur) => {
      if (dateBounds.max == null) return null;
      if (!cur || cur > dateBounds.max || cur < dateBounds.min) return dateBounds.max;
      return cur;
    });
  }, [dateBounds.min, dateBounds.max]);

  // Match list narrowed to the date range (applied on top of the competition
  // filter). This is the list the picker shows and the source of marker-id
  // collection downstream.
  const matchesList = useMemo(() => {
    if (!startDate && !endDate) return matchesInSeasonForCompetition;
    return matchesInSeasonForCompetition.filter((m) => {
      const d = String(m.date).slice(0, 10);
      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      return true;
    });
  }, [matchesInSeasonForCompetition, startDate, endDate]);

  // Reconcile selectedMatchIds with matchesList:
  // - first load (size=0) → default to all
  // - season/date-range change (most ids invalidate) → default to all in new list
  // - user explicitly cleared and matchesList unchanged → stay empty (effect won't re-run)
  useEffect(() => {
    if (matchesList.length === 0) return;
    setSelectedMatchIds((prev) => {
      if (prev.size === 0) {
        return new Set(matchesList.map((m) => String(m.id)));
      }
      const valid = new Set(matchesList.map((m) => String(m.id)));
      const next = new Set();
      prev.forEach((id) => { if (valid.has(id)) next.add(id); });
      if (next.size === 0) {
        matchesList.forEach((m) => next.add(String(m.id)));
      }
      return next;
    });
  }, [matchesList]);

  // Switching categories clears the param-level selection so checkboxes
  // from a previous category don't leak into the new one's filter. Skip the
  // first run so sessionStorage-restored or URL-param-driven selections
  // survive the initial render (SMRTSTATS-297 Events §3).
  const skipCategoryParamReset = useRef(true);
  useEffect(() => {
    if (skipCategoryParamReset.current) {
      skipCategoryParamReset.current = false;
      return;
    }
    setSelectedParamIds(new Set());
  }, [selectedCategory]);

  const matchRowsByMatchId = useMemo(() => {
    const rows = Array.isArray(matchStats)
      ? matchStats
      : Array.isArray(matchStats?.results) ? matchStats.results : [];
    const map = new Map();
    rows.forEach((r) => {
      if (r?.id != null) map.set(String(r.id), r);
    });
    return map;
  }, [matchStats]);

  const player = overview?.player ?? overview;

  // Union of marker IDs across selected matches.
  const markerIds = useMemo(() => {
    const ids = new Set();
    selectedMatchIds.forEach((matchId) => {
      const row = matchRowsByMatchId.get(matchId);
      if (!row) return;
      collectMarkerIds(row).forEach((id) => ids.add(id));
    });
    return Array.from(ids);
  }, [selectedMatchIds, matchRowsByMatchId]);

  // Marker -> param-id index across all selected matches (merged).
  const markerParamIndex = useMemo(() => {
    const merged = new Map();
    selectedMatchIds.forEach((matchId) => {
      const row = matchRowsByMatchId.get(matchId);
      if (!row) return;
      buildMarkerParamIndex(row).forEach((paramSet, mid) => {
        if (!merged.has(mid)) merged.set(mid, new Set());
        const target = merged.get(mid);
        paramSet.forEach((pid) => target.add(pid));
      });
    });
    return merged;
  }, [selectedMatchIds, matchRowsByMatchId]);

  // Bulk fetch markers for the union, chunked to keep each request under the
  // stage API's per-request marker budget (~MARKERS_CHUNK_SIZE). Refetches
  // when body part changes (markers_search filters server-side by body_part_id).
  // Chunks fire in parallel and their events arrays are concatenated;
  // buildEventsFromMarkers dedupes by marker id, so cross-chunk overlaps
  // (shouldn't happen but cheap to guard against) collapse to one event.
  useEffect(() => {
    if (markerIds.length === 0) {
      setEvents([]);
      return undefined;
    }
    let cancelled = false;
    setEventsLoading(true);
    const opts = selectedBodyPart != null ? { bodyPartId: [selectedBodyPart] } : {};
    const chunks = [];
    for (let i = 0; i < markerIds.length; i += MARKERS_CHUNK_SIZE) {
      chunks.push(markerIds.slice(i, i + MARKERS_CHUNK_SIZE));
    }
    Promise.all(
      chunks.map((chunk) => retryAsync(() => fetchMarkersBulk(chunk, opts))),
    )
      .then((responses) => {
        if (cancelled) return;
        const merged = responses.flatMap((res) => res.data?.events ?? []);
        setEvents(buildEventsFromMarkers(merged));
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false);
      });
    return () => { cancelled = true; };
  }, [markerIds, selectedBodyPart]);

  // Param groups keyed by name -> Set<paramId> for category filtering.
  const groupedParams = useMemo(() => {
    if (!Array.isArray(parameters) || parameters.length === 0) return [];
    return buildParamGroupsForPlayer(parameters, player);
  }, [parameters, player]);

  const groupParamIds = useMemo(() => {
    const map = new Map();
    groupedParams.forEach(([groupName, items]) => {
      map.set(groupName, new Set(items.map((it) => Number(it.id))));
    });
    return map;
  }, [groupedParams]);

  // groupName -> raw item array (for the second-level param picker).
  const groupItems = useMemo(() => {
    const map = new Map();
    groupedParams.forEach(([groupName, items]) => map.set(groupName, items));
    return map;
  }, [groupedParams]);

  // Per-param event count across the current selection. One pass over events.
  const paramEventCounts = useMemo(() => {
    const counts = new Map();
    events.forEach((e) => {
      const mp = markerParamIndex.get(e.markerId);
      if (!mp) return;
      mp.forEach((pid) => counts.set(pid, (counts.get(pid) ?? 0) + 1));
    });
    return counts;
  }, [events, markerParamIndex]);

  // Items in the active category that have ≥1 event in scope.
  const availableParamsForCategory = useMemo(() => {
    if (!selectedCategory) return [];
    const items = groupItems.get(selectedCategory) ?? [];
    return items
      .map((p) => {
        const id = Number(p.id);
        return { id, name: p.name, count: paramEventCounts.get(id) ?? 0 };
      })
      .filter((p) => p.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [selectedCategory, groupItems, paramEventCounts]);

  const availableCategories = useMemo(() => {
    if (events.length === 0) return [];
    const out = [];
    // Prepend the synthetic Goals chip when any in-scope event is a goal.
    // Sits first in the strip because it's the most-asked-for filter
    // (SMRTSTATS-297 Events §1).
    const hasGoals = events.some((e) => {
      const mp = markerParamIndex.get(e.markerId);
      if (!mp) return false;
      for (const pid of mp) if (GOALS_PARAM_IDS.has(pid)) return true;
      return false;
    });
    if (hasGoals) out.push(GOALS_CATEGORY);
    groupParamIds.forEach((paramSet, groupName) => {
      const has = events.some((e) => {
        const mp = markerParamIndex.get(e.markerId);
        if (!mp) return false;
        for (const pid of mp) if (paramSet.has(pid)) return true;
        return false;
      });
      if (has) out.push(groupName);
    });
    return out;
  }, [events, groupParamIds, markerParamIndex]);

  // Drop a stale category once events have hydrated. Skip while events are
  // still empty so a deep-link selection (?param= or sessionStorage restore)
  // isn't wiped before the first marker batch arrives.
  useEffect(() => {
    if (events.length === 0) return;
    if (selectedCategory && !availableCategories.includes(selectedCategory)) {
      setSelectedCategory(null);
    }
  }, [selectedCategory, availableCategories, events.length]);

  // Quick-access mode → set of param IDs.
  //   offensive = "In offence" ∪ "Goals/Chances/Shots"
  //   defensive = "In defence"
  // null when "all" so we know to skip the filter entirely.
  const modeParamIds = useMemo(() => {
    if (selectedMode === 'all') return null;
    const merge = (names) => {
      const out = new Set();
      names.forEach((name) => {
        const set = groupParamIds.get(name);
        if (set) set.forEach((id) => out.add(id));
      });
      return out;
    };
    if (selectedMode === 'offensive') return merge(['In offence', 'Goals/Chances/Shots']);
    if (selectedMode === 'defensive') return merge(['In defence']);
    return null;
  }, [selectedMode, groupParamIds]);

  // Heuristic positive/negative classification by param name. Markers don't
  // carry an explicit polarity field, so we match the conventional naming used
  // throughout the platform (Accurate/Inaccurate prefixes, + / - suffixes,
  // and the handful of standalone signals like Goal / Foul / Lost / Recovered).
  const eventTone = (paramName) => {
    const s = String(paramName || '');
    if (/^(Inaccurate|Lost|Missed|Failed|Conceded)\b/i.test(s)) return 'negative';
    if (/\s-\s*$/.test(s) || /\bFoul\b/i.test(s) || /\bYellow card\b/i.test(s) || /\bRed card\b/i.test(s)) return 'negative';
    if (/^(Accurate|Recovered|Successful|Won|Save|Saved|Goal|Assist)\b/i.test(s)) return 'positive';
    if (/\s\+\s*$/.test(s) || /\bTackle\b/i.test(s)) return 'positive';
    return 'neutral';
  };

  // Filter chain (each step narrows the previous):
  //   specific params  →  category  →  mode (off/def)  →  tone (pos/neg)
  // Specific params still take precedence over category, mirroring the
  // existing two-level chip + checkbox interaction.
  const filteredEvents = useMemo(() => {
    let pool = events;
    if (selectedParamIds.size > 0) {
      pool = pool.filter((e) => {
        const mp = markerParamIndex.get(e.markerId);
        if (!mp) return false;
        for (const pid of mp) if (selectedParamIds.has(pid)) return true;
        return false;
      });
    } else if (selectedCategory !== null) {
      // Synthetic Goals category resolves to its hardcoded id set; everything
      // else looks up the real backend group.
      const paramSet = selectedCategory === GOALS_CATEGORY
        ? GOALS_PARAM_IDS
        : groupParamIds.get(selectedCategory);
      if (paramSet) {
        pool = pool.filter((e) => {
          const mp = markerParamIndex.get(e.markerId);
          if (!mp) return false;
          for (const pid of mp) if (paramSet.has(pid)) return true;
          return false;
        });
      }
    }
    if (modeParamIds) {
      pool = pool.filter((e) => {
        const mp = markerParamIndex.get(e.markerId);
        if (!mp) return false;
        for (const pid of mp) if (modeParamIds.has(pid)) return true;
        return false;
      });
    }
    if (selectedTone !== 'all') {
      pool = pool.filter((e) => eventTone(e.paramName) === selectedTone);
    }
    return pool;
  }, [events, selectedCategory, selectedParamIds, groupParamIds, markerParamIndex, modeParamIds, selectedTone]);

  if (!mappedPlayerId) {
    return <Navigate to="/player/not-mapped" replace />;
  }

  const toggleEvent = (key) => {
    setSelectedEventKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleMatch = (matchId) => {
    setSelectedMatchIds((prev) => {
      const next = new Set(prev);
      const id = String(matchId);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllMatches = () => {
    setSelectedMatchIds(new Set(matchesList.map((m) => String(m.id))));
  };

  const clearMatches = () => setSelectedMatchIds(new Set());

  const selectAllVisibleEvents = () => {
    setSelectedEventKeys(new Set(filteredEvents.map((e) => e.key)));
  };

  const clearEventSelection = () => setSelectedEventKeys(new Set());

  const toggleAllInGroup = (groupEventKeys, allCurrentlySelected) => {
    setSelectedEventKeys((prev) => {
      const next = new Set(prev);
      if (allCurrentlySelected) {
        groupEventKeys.forEach((k) => next.delete(k));
      } else {
        groupEventKeys.forEach((k) => next.add(k));
      }
      return next;
    });
  };

  const toggleParam = (paramId) => {
    setSelectedParamIds((prev) => {
      const next = new Set(prev);
      const id = Number(paramId);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const clearParams = () => setSelectedParamIds(new Set());

  const watchSelected = async () => {
    if (selectedEventKeys.size === 0 || creatingShareLink) return;
    setCreatingShareLink(true);
    try {
      // Map event keys (= marker IDs as strings) back to numbers for the API.
      const ids = Array.from(selectedEventKeys).map((k) => Number(k)).filter(Number.isFinite);
      const fullName = player?.display_name
        || [player?.name, player?.surname].filter(Boolean).join(' ')
        || player?.name
        || l(1800);
      const res = await createVideoList(ids, `${l(9)} - ${fullName}`);
      const token = res.data?.token;
      if (token) {
        navigate(`/player/events/video?token=${token}`);
      }
    } catch {
      // Surface as inline error if needed; for now a no-op keeps the CTA usable.
    } finally {
      setCreatingShareLink(false);
    }
  };

  return (
    <Wrap>
      <Title>{l(1832)}</Title>

      {error && <ErrorBox>{l(1860)}</ErrorBox>}
      {loading && <PlayerLoader label={l(1831)} />}

      {!loading && (
        <>
          {seasons.length > 1 && (
            <FilterRow>
              <FilterLabel>{l(453)}</FilterLabel>
              <Select
                value={selectedSeasonId ?? ''}
                onChange={(e) => {
                  setSelectedSeasonId(e.target.value);
                  setSelectedMatchIds(new Set());
                  setSelectedEventKeys(new Set());
                  setSelectedParamIds(new Set());
                  // Drop the persisted date range so the new season's bounds
                  // effect can refill it from the new dateBounds (otherwise
                  // a single-day window from the previous season survives).
                  setStartDate(null);
                  setEndDate(null);
                }}
              >
                {seasons.map((s) => (
                  <option key={s.id} value={s.id}>{s.years ?? s.name ?? s.id}</option>
                ))}
              </Select>
            </FilterRow>
          )}

          {competitions.length > 1 && (
            <FilterRow>
              <FilterLabel>{l(454)}</FilterLabel>
              <Select
                value={selectedCompetitionId}
                onChange={(e) => {
                  setSelectedCompetitionId(e.target.value);
                  setSelectedMatchIds(new Set());
                  setSelectedEventKeys(new Set());
                  setSelectedParamIds(new Set());
                  // Same rationale as the season switcher above: the
                  // competition narrows the match list, which changes
                  // dateBounds — wipe so the bounds effect picks new defaults.
                  setStartDate(null);
                  setEndDate(null);
                }}
              >
                <option value="">{l(1704)}</option>
                {competitions.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </FilterRow>
          )}

          {dateBounds.min && dateBounds.max && (
            <DateRangeRow>
              <DateGroup>
                <FilterLabel>{l(1852)}</FilterLabel>
                <DateInput
                  type="date"
                  value={startDate ?? ''}
                  min={dateBounds.min}
                  max={endDate ?? dateBounds.max}
                  onChange={(e) => setStartDate(e.target.value || dateBounds.min)}
                />
              </DateGroup>
              <DateGroup>
                <FilterLabel>{l(1853)}</FilterLabel>
                <DateInput
                  type="date"
                  value={endDate ?? ''}
                  min={startDate ?? dateBounds.min}
                  max={dateBounds.max}
                  onChange={(e) => setEndDate(e.target.value || dateBounds.max)}
                />
              </DateGroup>
            </DateRangeRow>
          )}

          <SectionHeader>
            <SectionLabel>{l(7)} · {selectedMatchIds.size} / {matchesList.length}</SectionLabel>
            <SectionActions>
              {selectedMatchIds.size < matchesList.length && (
                <ActionLink type="button" onClick={selectAllMatches}>{l(1833)}</ActionLink>
              )}
              {selectedMatchIds.size > 0 && (
                <ActionLink type="button" onClick={clearMatches}>{l(1023)}</ActionLink>
              )}
            </SectionActions>
          </SectionHeader>

          <Card>
            <ScrollList>
              {matchesList.map((m, idx) => {
                const id = String(m.id);
                const checked = selectedMatchIds.has(id);
                return (
                  <MatchRow key={id} $first={idx === 0}>
                    <Checkbox
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleMatch(id)}
                    />
                    <RowMain>
                      <MatchTeams>
                        {m.home_team} {m.score ?? `${m.home_team_score}:${m.away_team_score}`} {m.away_team}
                      </MatchTeams>
                      <MatchMeta>
                        {m.competition} · <Moment format="DD.MM.YYYY">{m.date}</Moment>
                      </MatchMeta>
                    </RowMain>
                  </MatchRow>
                );
              })}
            </ScrollList>
          </Card>

          {events.length > 0 && (
            <QuickAccessRows>
              <QuickAccessRow>
                <QuickAccessLabel>{l(980)}</QuickAccessLabel>
                <CategoryChip
                  type="button"
                  $selected={selectedMode === 'all'}
                  onClick={() => setSelectedMode('all')}
                >
                  {l(1837)}
                </CategoryChip>
                <CategoryChip
                  type="button"
                  $selected={selectedMode === 'offensive'}
                  onClick={() => setSelectedMode('offensive')}
                >
                  {l(1855)}
                </CategoryChip>
                <CategoryChip
                  type="button"
                  $selected={selectedMode === 'defensive'}
                  onClick={() => setSelectedMode('defensive')}
                >
                  {l(1856)}
                </CategoryChip>
              </QuickAccessRow>
              <QuickAccessRow>
                <QuickAccessLabel>{l(1854)}</QuickAccessLabel>
                <CategoryChip
                  type="button"
                  $selected={selectedTone === 'all'}
                  onClick={() => setSelectedTone('all')}
                >
                  {l(1837)}
                </CategoryChip>
                <CategoryChip
                  type="button"
                  $selected={selectedTone === 'positive'}
                  onClick={() => setSelectedTone('positive')}
                >
                  {l(1857)}
                </CategoryChip>
                <CategoryChip
                  type="button"
                  $selected={selectedTone === 'negative'}
                  onClick={() => setSelectedTone('negative')}
                >
                  {l(1858)}
                </CategoryChip>
              </QuickAccessRow>
            </QuickAccessRows>
          )}

          {availableCategories.length > 0 && (
            <CategoryStrip>
              <CategoryChip
                type="button"
                $selected={selectedCategory === null}
                onClick={() => setSelectedCategory(null)}
              >
                {l(1837)} · {events.length}
              </CategoryChip>
              {availableCategories.map((groupName) => (
                <CategoryChip
                  key={groupName}
                  type="button"
                  $selected={selectedCategory === groupName}
                  onClick={() => setSelectedCategory(groupName)}
                >
                  {groupName}
                </CategoryChip>
              ))}
            </CategoryStrip>
          )}

          {selectedCategory && availableParamsForCategory.length > 0 && (
            <>
              <SectionHeader>
                <SectionLabel>{selectedCategory}</SectionLabel>
                <SectionActions>
                  {selectedParamIds.size > 0 && (
                    <ActionLink type="button" onClick={clearParams}>{l(1023)}</ActionLink>
                  )}
                </SectionActions>
              </SectionHeader>
              <Card>
                <ScrollList>
                  {availableParamsForCategory.map((p, idx) => {
                    const checked = selectedParamIds.has(p.id);
                    return (
                      <ParamRow key={p.id} $first={idx === 0}>
                        <Checkbox
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleParam(p.id)}
                        />
                        <ParamMain>
                          <ParamName>{p.name}</ParamName>
                          <ParamCount>{p.count}</ParamCount>
                        </ParamMain>
                      </ParamRow>
                    );
                  })}
                </ScrollList>
              </Card>
            </>
          )}

          {/* Body-part filter — collapsed by default; tap to disclose chips.
              Sits above the events list so it reads as a filter that
              narrows what follows, not an unrelated bottom-of-page action. */}
          <Disclosure>
            <DisclosureToggle
              type="button"
              onClick={() => setBodyPartOpen((v) => !v)}
              aria-expanded={bodyPartOpen}
            >
              <DisclosureLabel>
                {l(1838)}
                {selectedBodyPart != null && (
                  <DisclosureValue>· {l(bodyPartLexId(selectedBodyPart))}</DisclosureValue>
                )}
              </DisclosureLabel>
              <DisclosureChevron $open={bodyPartOpen}>›</DisclosureChevron>
            </DisclosureToggle>

            {bodyPartOpen && (
              <BodyPartChips>
                {BODY_PARTS.map((bp) => {
                  const isSelected = bp.id === selectedBodyPart || (bp.id === null && selectedBodyPart === null);
                  return (
                    <CategoryChip
                      key={String(bp.id)}
                      type="button"
                      $selected={isSelected}
                      onClick={() => setSelectedBodyPart(bp.id)}
                    >
                      {l(bp.lid)}
                    </CategoryChip>
                  );
                })}
              </BodyPartChips>
            )}
          </Disclosure>

          <SectionHeader>
            <SectionLabel>{l(9)} · {filteredEvents.length}</SectionLabel>
            <SectionActions>
              {filteredEvents.length > 0 && selectedEventKeys.size < filteredEvents.length && (
                <ActionLink type="button" onClick={selectAllVisibleEvents}>{l(1833)}</ActionLink>
              )}
              {selectedEventKeys.size > 0 && (
                <ActionLink type="button" onClick={clearEventSelection}>{l(1023)}</ActionLink>
              )}
            </SectionActions>
          </SectionHeader>

          {/* Show the loader while EITHER the bootstrap matchStats fetch is
              still in flight (matchStats === null) OR the markers_search
              call is. Prevents the "No events for this filter." flash when
              the bootstrap effect was cancelled mid-fetch on first mount —
              the client-reported case where all matches show as selected
              but events count is 0 until the user reselects a filter. */}
          {(matchStats === null || (eventsLoading && events.length === 0)) && (
            <PlayerLoader label={l(1831)} />
          )}

          {matchStats !== null && !eventsLoading && filteredEvents.length === 0 && (
            <Muted>
              {selectedMatchIds.size === 0
                ? l(1859)
                : 'No events for this filter.'}
            </Muted>
          )}

          {filteredEvents.length > 0 && (
            <GroupsWrap>
              {groupEventsByMatch(filteredEvents).map((group) => {
                const groupKeys = group.events.map((e) => e.key);
                const groupSelectedCount = groupKeys.filter((k) => selectedEventKeys.has(k)).length;
                const allSelected = groupKeys.length > 0 && groupSelectedCount === groupKeys.length;
                const someSelected = groupSelectedCount > 0 && !allSelected;
                return (
                  <Group key={group.match?.id ?? 'orphan'}>
                    {group.match && (
                      <GroupHeader>
                        <Checkbox
                          type="checkbox"
                          checked={allSelected}
                          ref={(el) => { if (el) el.indeterminate = someSelected; }}
                          onChange={() => toggleAllInGroup(groupKeys, allSelected)}
                          aria-label={`Toggle all events in ${group.match.homeTeam} vs ${group.match.awayTeam}`}
                        />
                        <GroupHeaderMain>
                          <GroupTeams>
                            {group.match.homeTeam} {group.match.score ?? ''} {group.match.awayTeam}
                          </GroupTeams>
                          <GroupMeta>
                            {group.match.date && <Moment format="DD.MM.YYYY">{group.match.date}</Moment>}
                            <GroupCount>{groupSelectedCount} / {groupKeys.length}</GroupCount>
                          </GroupMeta>
                        </GroupHeaderMain>
                      </GroupHeader>
                    )}
                    <Card>
                      <ScrollList>
                        {group.events.map((e, idx) => {
                          const checked = selectedEventKeys.has(e.key);
                          return (
                            <EventRow key={e.key} $first={idx === 0}>
                              <Checkbox
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleEvent(e.key)}
                              />
                              <EventMain>
                                <EventName>{e.paramName}</EventName>
                                <EventTime>{formatSeconds(e.second)}</EventTime>
                              </EventMain>
                            </EventRow>
                          );
                        })}
                      </ScrollList>
                    </Card>
                  </Group>
                );
              })}
            </GroupsWrap>
          )}
        </>
      )}

      {selectedEventKeys.size > 0 && (
        <WatchBar>
          <WatchButton
            type="button"
            onClick={watchSelected}
            disabled={creatingShareLink}
          >
            {creatingShareLink ? (
              <InlinePlayerLoader />
            ) : (
              <PlayIcon src={play} alt="" />
            )}
            {creatingShareLink ? l(1827) : l(1835).replace('{count}', String(selectedEventKeys.size))}
          </WatchButton>
        </WatchBar>
      )}
    </Wrap>
  );
};

export default PlayerEvents;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 80px; /* room for the sticky watch bar */
`;

const Title = styled.h1`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 22px;
  margin: 0;
  color: ${(props) => props.theme.white};
`;

const Muted = styled.div`
  color: ${(props) => props.theme.blueish2};
  font-size: 14px;
`;

const ErrorBox = styled.div`
  color: ${(props) => props.theme.white};
  background: #3a1414;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
`;

// Filter bar — lives between the title and the first content card.
const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 4px;
`;

const FilterLabel = styled.span`
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
  text-transform: uppercase;
  letter-spacing: 0.6px;
  flex: 0 0 auto;
`;

const Select = styled.select`
  flex: 1;
  /* Without min-width:0 a long option label expands the select past its
     container — reproduced on desktop with "United Arab Emirates. Pro
     League". The text-overflow rule clips the visible label to ellipsis
     inside the trigger while the dropdown list itself still shows full
     names. */
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background: ${(props) => props.theme.row};
  color: ${(props) => props.theme.white};
  border-radius: 10px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.blueish2};
  }
`;

// Two date inputs side-by-side, From and To, sharing the FilterLabel style.
const DateRangeRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 0 4px;
`;

const DateGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const DateInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background: ${(props) => props.theme.row};
  color: ${(props) => props.theme.white};
  border-radius: 10px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
  color-scheme: dark;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.blueish2};
  }
`;

// SectionHeader — small uppercase label outside the card with right-aligned
// links. Mirrors the matches page's competition-strip pattern.
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 4px 0;
  margin-top: 4px;
`;

const SectionLabel = styled.h3`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: ${(props) => props.theme.blueish2};
  margin: 0;
`;

const SectionActions = styled.div`
  display: flex;
  gap: 14px;
`;

const ActionLink = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  color: ${(props) => props.theme.green};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;

  &:hover { opacity: 0.8; }
`;

// Card — the rounded container for either match list or events list.
// No padding; rows handle their own horizontal/vertical inset and dividers.
const Card = styled.div`
  display: flex;
  flex-direction: column;
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 12px;
  overflow: hidden;
`;

// ScrollList — internal scroll inside a Card so long lists don't push the
// page into endless-scroll territory. Two variants:
//   default: ~4 rows visible (matches and parameter pickers)
//   $tall:   ~7-8 rows visible (events list, where you want more scanability)
//
// Slim neutral scrollbar (the global * scrollbar in style/Global.js is a
// chunky 14px lime thumb — too loud inside our cards).
const ScrollList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: ${(props) => (props.$tall ? '480px' : '240px')};
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: ${(props) => props.theme.borderColor} transparent;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 0;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.borderColor};
    border: 0;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background-color: ${(props) => props.theme.blueish2};
  }
`;

const MatchRow = styled.label`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  cursor: pointer;
  border-top: 1px solid ${(props) => props.theme.borderColor};

  ${(props) => props.$first && `border-top: 0;`}

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${(props) => props.theme.green};
  flex: 0 0 auto;
  cursor: pointer;
`;

const RowMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const MatchTeams = styled.div`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
  color: ${(props) => props.theme.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`;

const MatchMeta = styled.div`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// Quick-access rows — two stacked label+chips groups (Mode / Tone) that act
// as a primary entry filter. Sit above the category strip so the player can
// land in "defensive moments" or "my positive actions" without scrolling
// through the full category list.
const QuickAccessRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
`;

const QuickAccessRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const QuickAccessLabel = styled.span`
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
  text-transform: uppercase;
  letter-spacing: 0.6px;
  flex: 0 0 60px;
`;

// Category chips — wider hit area, hairline border, accent only when selected.
const CategoryStrip = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 2px 4px 6px;
  margin: 0 -16px;
  padding-left: 16px;
  padding-right: 16px;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const CategoryChip = styled.button`
  flex: 0 0 auto;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid ${(props) => (props.$selected ? props.theme.green : props.theme.borderColor)};
  background: ${(props) => (props.$selected ? props.theme.green : 'transparent')};
  color: ${(props) => (props.$selected ? props.theme.black : props.theme.white)};
  font-family: ${(props) => (props.$selected ? "'SatoshiBold', sans-serif" : "'SatoshiMedium', sans-serif")};
  font-size: 12px;
  letter-spacing: 0.2px;
  white-space: nowrap;
  cursor: pointer;
  scroll-snap-align: start;
  transition: border-color 120ms ease, background 120ms ease;
`;

// Match-grouped events list — same layout as the video player page.
// Each match gets its own card; the group header has a tri-state checkbox
// that selects/deselects all events from that match in one tap, plus a
// small "N / M" counter showing how many of the match's events are
// already in the selection.
const GroupsWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupHeader = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 4px;
  cursor: pointer;
`;

const GroupHeaderMain = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const GroupTeams = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
  color: ${(props) => props.theme.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const GroupMeta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
  text-transform: uppercase;
  letter-spacing: 0.4px;
  flex-shrink: 0;

  /* color: inherit on children so the global "* { color: theme.green }" rule
     doesn't repaint the nested Moment span lime. */
  & > * { color: inherit; }
`;

const GroupCount = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid ${(props) => props.theme.borderColor};
  color: ${(props) => props.theme.blueish2};
  font-size: 10px;
  letter-spacing: 0.6px;
`;

// Param picker rows — second-level filter inside a category.
// Same row pattern as MatchRow but with a count badge on the right.
const ParamRow = styled.label`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  cursor: pointer;
  border-top: 1px solid ${(props) => props.theme.borderColor};

  ${(props) => props.$first && `border-top: 0;`}

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const ParamMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;

const ParamName = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
  color: ${(props) => props.theme.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const ParamCount = styled.span`
  flex-shrink: 0;
  font-family: 'SatoshiBold', sans-serif;
  font-size: 11px;
  letter-spacing: 0.4px;
  color: ${(props) => props.theme.blueish2};
  background: rgba(255, 255, 255, 0.04);
  padding: 2px 8px;
  border-radius: 999px;
`;

// Disclosure — collapsible section that hides its content by default.
// Used for the body-part filter so it doesn't eat vertical space when unused.
const Disclosure = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
`;

const DisclosureToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  background: ${(props) => props.theme.row};
  border: 1px solid ${(props) => props.theme.borderColor};
  border-radius: 12px;
  color: ${(props) => props.theme.white};
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: border-color 120ms ease;

  &:hover { border-color: ${(props) => props.theme.blueish2}; }
`;

const DisclosureLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-size: 11px;
  color: ${(props) => props.theme.blueish2};
`;

const DisclosureValue = styled.span`
  color: ${(props) => props.theme.white};
  text-transform: none;
  letter-spacing: 0.2px;
  font-size: 13px;
`;

const DisclosureChevron = styled.span`
  font-size: 18px;
  line-height: 1;
  color: ${(props) => props.theme.blueish2};
  transform: rotate(${(props) => (props.$open ? '90deg' : '0')});
  transition: transform 160ms ease;
`;

const BodyPartChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 4px;
`;

// Event rows — same row pattern as matches but single-line.
const EventRow = styled.label`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
  cursor: pointer;
  border-top: 1px solid ${(props) => props.theme.borderColor};

  ${(props) => props.$first && `border-top: 0;`}

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
`;

const EventMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const EventName = styled.span`
  font-family: 'SatoshiMedium', sans-serif;
  font-size: 14px;
  color: ${(props) => props.theme.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const EventTime = styled.span`
  font-family: 'SatoshiBold', sans-serif;
  font-size: 12px;
  color: ${(props) => props.theme.blueish2};
  letter-spacing: 0.3px;
  flex-shrink: 0;
`;

const WatchBar = styled.div`
  position: fixed;
  left: 50%;
  transform: translateX(-50%);
  /* Float clearly above the bottom nav (~60px tall + safe-area) — extra
     gap so the pill sits in the empty space rather than touching the nav. */
  bottom: calc(96px + env(safe-area-inset-bottom, 0px));
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 480px;
  padding: 0 16px;
  box-sizing: border-box;
  pointer-events: none;
  z-index: 11;
`;

const WatchButton = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 22px;
  border: 0;
  border-radius: 999px;
  background: ${(props) => props.theme.green};
  color: ${(props) => props.theme.black};
  font-family: 'SatoshiBold', sans-serif;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);

  &:disabled { opacity: 0.6; cursor: default; }
`;

// The bundled SVG is white/light; brightness(0) repaints every fill to
// black so it's visible against the lime green pill background.
const PlayIcon = styled.img`
  width: 12px;
  height: 12px;
  filter: brightness(0);
`;
